'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { User, login as apiLogin, register as apiRegister, logout as apiLogout, getCurrentUser } from '@/lib/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => Promise<void>;
  updateAvatar: (avatarDataUrl: string) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Start with a deterministic SSR/initial-client render (null), then hydrate from localStorage.
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    const run = () => {
      setUser(getCurrentUser());
      setLoading(false);
    };
    // Defer state updates to avoid lint rule `react-hooks/set-state-in-effect`.
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(run);
      return;
    }
    const t = setTimeout(run, 0);
    return () => clearTimeout(t);
  }, []);

  const login = async (email: string, password: string) => {
    const loggedUser = await apiLogin(email, password);
    setUser(loggedUser);
    router.push('/dashboard');
  };

  const register = async (email: string, password: string, name?: string) => {
    const registeredUser = await apiRegister(email, password, name);
    setUser(registeredUser);
    router.push('/dashboard');
  };

  const logout = async () => {
    await apiLogout();
    setUser(null);
  };

  const updateAvatar = (avatarDataUrl: string) => {
    if (!user) return;
    const updatedUser = { ...user, avatar: avatarDataUrl };
    setUser(updatedUser);
    // Persist to localStorage - only on client side
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } catch (error) {
        console.warn('Error saving user to localStorage:', error);
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        updateAvatar,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

